/**
 * Pure helpers for the Product Catalog page (catalog-mapping v1). Mapping-state + blank-resolve
 * status semantics, the placement-location options, a catalog summary, and the editor-draft ↔
 * save-request conversion + validation. Kept DOM-free so it's unit-tested in isolation.
 */
import type { components } from '@/lib/api/types.gen';

export type CatalogProductSummary = components['schemas']['CatalogProductSummary'];
export type CapCatalogProduct = components['schemas']['CapCatalogProduct'];
export type CatalogVariantSummary = components['schemas']['CatalogVariantSummary'];
export type ProductMappingView = components['schemas']['ProductMappingView'];
export type ProductPlacement = ProductMappingView['placements'][number];
export type ProductMappingSaveRequest = components['schemas']['ProductMappingSaveRequest'];
export type VariantResolveResult = components['schemas']['VariantResolveResult'];
export type CatalogMappingState = CatalogProductSummary['mappingState'];
export type BlankResolveStatus = CatalogVariantSummary['blankResolveStatus'];

export type StatusMeta = { label: string; className: string };

/**
 * Size a Shopify product-image URL for the box it renders in. The catalog grid + mapping header show
 * the featured image in a 56/80px thumbnail, but the stored URL is the raw full-resolution Shopify
 * CDN original (often 1500–2048px, ~MB) — the Catalog page's "images load slowly" cause. Shopify's
 * image CDN resizes on the fly via a `width` query param, so requesting the box size (2× for retina)
 * cuts each thumbnail from megabytes to a few KB. Non-Shopify / unparseable URLs are returned as-is
 * (the param is harmless if the host ignores it); the full-res original stays untouched for the
 * lightbox, which passes the raw URL directly.
 */
export function withImageWidth(url: string, px: number): string {
  try {
    const u = new URL(url);
    u.searchParams.set('width', String(px));
    return u.toString();
  } catch {
    return url;
  }
}

const NEUTRAL = 'border-border bg-muted text-muted-foreground';
const GREEN =
  'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300';
const AMBER =
  'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300';

/** Colour is meaning: green = every variant resolved, amber = some still open, neutral = unmapped. */
export const CATALOG_MAPPING_STATE_META: Record<CatalogMappingState, StatusMeta> = {
  MAPPED: { label: 'Mapped', className: GREEN },
  PARTIAL: { label: 'Partial', className: AMBER },
  UNMAPPED: { label: 'Unmapped', className: NEUTRAL },
};

export const CATALOG_BLANK_RESOLVE_META: Record<BlankResolveStatus, StatusMeta> = {
  RESOLVED: { label: 'Resolved', className: GREEN },
  AMBIGUOUS: { label: 'Ambiguous', className: AMBER },
  UNRESOLVED: { label: 'Unresolved', className: NEUTRAL },
};

/** Seed options for the location Select — the API accepts any lowercase slug, this is the common set. */
export const PLACEMENT_LOCATION_OPTIONS = [
  'front',
  'back',
  'left-chest',
  'right-chest',
  'sleeve',
] as const;

export const MAX_PLACEMENTS = 12;

export type CatalogCounts = { total: number; mapped: number; partial: number; unmapped: number };

export function summarizeCatalog(products: readonly CatalogProductSummary[]): CatalogCounts {
  const counts: CatalogCounts = { total: products.length, mapped: 0, partial: 0, unmapped: 0 };
  for (const p of products) {
    if (p.mappingState === 'MAPPED') counts.mapped += 1;
    else if (p.mappingState === 'PARTIAL') counts.partial += 1;
    else counts.unmapped += 1;
  }
  return counts;
}

/** Whether the product has any youth-band variant — drives the "set a youth blank style" hint. */
export function hasYouthVariants(variants: readonly CatalogVariantSummary[]): boolean {
  return variants.some((v) => v.sizeBand === 'youth');
}

/**
 * The distinct variant colorways for a product — the keys a per-colorway placement maps
 * (catalog-mapping v1.2). Returns the raw `variant.color` strings (NOT trimmed) so a key seeded here
 * matches the variant colour byte-for-byte at batch build; empty/absent colours are dropped.
 */
export function distinctColorways(variants: readonly CatalogVariantSummary[]): string[] {
  const seen = new Set<string>();
  for (const v of variants) {
    if (v.color != null && v.color.length > 0) seen.add(v.color);
  }
  return [...seen].sort();
}

/**
 * Editor row state. A placement is a location + a design — there is NO size here (print size comes
 * from the design file's own pixels; halftones are never scaled, catalog-mapping v1.1). When
 * `designByColor` is present the placement is in per-colorway mode (catalog-mapping v1.2): the design
 * varies by the product's variant colorway, keyed by the Shopify variant colour VERBATIM. `designId`
 * stays as the required default/anchor.
 */
export type PlacementDraft = {
  location: string;
  designId: string;
  designByColor?: Record<string, string>;
  /**
   * Decoration technique. `undefined` and `'dtf'` mean the same thing to the server — the field is
   * sparse by contract, so {@link buildMappingSaveRequest} emits it ONLY for `'screen-print'` and
   * every existing mapping keeps saving byte-identical. The editor normalizes to `'dtf'` for the
   * control's value so the Select is never uncontrolled.
   */
  method?: 'dtf' | 'screen-print';
};

export function emptyPlacementDraft(): PlacementDraft {
  // No `method` — a new row defaults to DTF by absence, which is what the contract means by sparse.
  return { location: '', designId: '' };
}

/** The technique a draft row will save as. Absent ⇒ dtf, mirroring the server's reading of the field. */
export function draftMethod(d: Pick<PlacementDraft, 'method'>): 'dtf' | 'screen-print' {
  return d.method === 'screen-print' ? 'screen-print' : 'dtf';
}

/** Seed the editor from an existing mapping's placements (carries a per-colorway map if present). */
export function draftFromPlacement(p: ProductPlacement): PlacementDraft {
  return {
    location: p.location,
    designId: p.designId,
    ...(p.designByColor ? { designByColor: { ...p.designByColor } } : {}),
    ...(p.method === 'screen-print' ? { method: 'screen-print' as const } : {}),
  };
}

export type BuildResult =
  | { ok: true; request: ProductMappingSaveRequest }
  | { ok: false; error: string };

/**
 * Validate + convert the editor state into the PUT body. Mirrors the server's Zod contract so the
 * operator gets an inline error instead of a 400: blankStyle required; 1–12 placements; each needs a
 * location slug + a design; locations unique. Sizing lives on the design file, not the placement.
 * `blankColor` is optional — the operator-picked S&S colour used for both bands (empty → the variant
 * colour is used at resolve time).
 *
 * `colorways` is the product's full distinct colorway list. A per-colorway placement serializes an
 * entry ONLY for the colorways the operator has actually mapped — PARTIAL maps are allowed so a
 * product can be mapped incrementally as its art is uploaded (an unmapped colorway is omitted, and
 * its orders BLOCK at batch build until its design is added — never a wrong/default print). At least
 * one colorway must be mapped (else turn the per-color toggle off). Keys are the product's colorways
 * VERBATIM; stale keys (colorways the product no longer has) are dropped. The full colorway list is
 * shown in the editor so an unmapped colorway is visible, not silent.
 */
export function buildMappingSaveRequest(
  blankStyle: string,
  youthBlankStyle: string,
  blankColor: string,
  drafts: readonly PlacementDraft[],
  colorways: readonly string[] = [],
  /**
   * Per-colorway BLANK colours (the second per-colorway axis — see `blankColorByColor` in contracts).
   *
   * Three states, and the editor always states one explicitly rather than relying on the server's
   * "keep" default: a MAP turns the axis on (keys are the product's colorways verbatim; a colorway with
   * no colour is omitted and refuses at resolve, never given the fallback), and `null` turns it OFF.
   * `undefined` is only for callers that predate the field — it leaves whatever is stored alone, which
   * is what stops a browser tab on the old bundle from wiping the map on an unrelated save.
   */
  blankColorByColor?: Record<string, string> | null,
): BuildResult {
  const style = blankStyle.trim();
  if (style.length === 0) return { ok: false, error: 'Pick an S&S blank style.' };

  if (drafts.length === 0) return { ok: false, error: 'Add at least one placement.' };
  if (drafts.length > MAX_PLACEMENTS) {
    return { ok: false, error: `A product can have at most ${MAX_PLACEMENTS} placements.` };
  }

  const placements: ProductMappingSaveRequest['placements'] = [];
  const seenLocations = new Set<string>();
  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i];
    if (!d) continue;
    const rowLabel = `Placement ${i + 1}`;
    const location = d.location.trim().toLowerCase();
    if (location.length === 0) return { ok: false, error: `${rowLabel}: choose a location.` };
    if (!/^[a-z0-9-]+$/.test(location)) {
      return { ok: false, error: `${rowLabel}: location must be a lowercase slug (a-z, 0-9, -).` };
    }
    if (seenLocations.has(location)) {
      return { ok: false, error: `${rowLabel}: "${location}" is used more than once.` };
    }
    seenLocations.add(location);

    if (d.designId.trim().length === 0) return { ok: false, error: `${rowLabel}: pick a design.` };

    const placement: ProductMappingSaveRequest['placements'][number] = {
      location,
      designId: d.designId.trim(),
      // ⚠️ SPARSE ON PURPOSE — emitted only for screen print. Writing `'dtf'` explicitly would add an
      // attribute to all 26 existing mappings on their next save, and the whole point of the field's
      // optionality is that absence already means DTF.
      ...(draftMethod(d) === 'screen-print' ? { method: 'screen-print' as const } : {}),
    };
    // Per-colorway mode (v1.2): serialize an entry only for the colorways actually mapped. PARTIAL is
    // allowed (map what has art now; add the rest later) — an unmapped colorway is omitted and BLOCKS
    // at batch build, never a wrong/default print. Keys are the product's colorways VERBATIM (matched
    // byte-exact at batch time); only the design id is trimmed; stale keys are dropped.
    if (d.designByColor && Object.keys(d.designByColor).length > 0) {
      if (colorways.length === 0) {
        return { ok: false, error: `${rowLabel}: this product has no colorways to map per-color.` };
      }
      const byColor: Record<string, string> = {};
      for (const color of colorways) {
        const designId = (d.designByColor[color] ?? '').trim();
        if (designId.length > 0) byColor[color] = designId;
      }
      if (Object.keys(byColor).length === 0) {
        return {
          ok: false,
          error: `${rowLabel}: pick a design for at least one colorway, or turn off "different design per color".`,
        };
      }
      placement.designByColor = byColor;
    }
    placements.push(placement);
  }

  const youth = youthBlankStyle.trim();
  const color = blankColor.trim();

  // Per-colorway blanks. Same serialization rules as the per-colorway DESIGN map above — keys are the
  // product's colorways VERBATIM, values trimmed, unmapped colorways omitted rather than blank-filled,
  // stale keys dropped — because both are matched byte-exact server-side against the same variant
  // colour, so any normalisation here would silently stop matching.
  let byColor: Record<string, string> | null | undefined;
  if (blankColorByColor === null) {
    byColor = null; // explicit "off" — the server clears the stored map
  } else if (blankColorByColor !== undefined) {
    if (colorways.length === 0) {
      return { ok: false, error: 'This product has no colorways to give their own blank colour.' };
    }
    const picked: Record<string, string> = {};
    for (const c of colorways) {
      const value = (blankColorByColor[c] ?? '').trim();
      if (value.length > 0) picked[c] = value;
    }
    if (Object.keys(picked).length === 0) {
      return {
        ok: false,
        error:
          'Pick a blank colour for at least one colorway, or turn off "different blank colour per colour".',
      };
    }
    byColor = picked;
  }

  return {
    ok: true,
    request: {
      blankStyle: style,
      ...(youth.length > 0 ? { youthBlankStyle: youth } : {}),
      ...(color.length > 0 ? { blankColor: color } : {}),
      ...(byColor !== undefined ? { blankColorByColor: byColor } : {}),
      placements,
    },
  };
}

/**
 * Which colorways of a per-colour blank map name a colour the chosen style does NOT carry — the check
 * that turns "save, then discover 15 youth variants refused" into an inline warning.
 *
 * One declared colour serves BOTH bands (adult style + youth style), so a colour that exists on Tultex
 * 602 but not on Tultex 235 refuses every youth variant of that colorway. That is a real shape, not a
 * hypothetical: the owner still owes an answer on whether Tultex 235 carries the same four colours.
 *
 * ⚠️ Returns `null` — meaning UNKNOWN, say nothing — when the style's colour list hasn't loaded or
 * failed. An unknown must never render as a tick: this check is advisory (the server is the authority
 * and refuses correctly either way), so silence is right, but claiming "fine" on missing data is not.
 */
export function colorwaysMissingFromStyle(
  byColor: Readonly<Record<string, string>>,
  styleColorNames: readonly string[] | undefined,
): string[] | null {
  if (styleColorNames === undefined) return null;
  // A style with zero colours is also unknown rather than "nothing matches" — an empty list is what an
  // errored or still-warming query looks like, and every real S&S style carries at least one colour.
  if (styleColorNames.length === 0) return null;
  const available = new Set(styleColorNames);
  return Object.entries(byColor)
    .filter(([, colour]) => colour.trim().length > 0 && !available.has(colour.trim()))
    .map(([colorway]) => colorway);
}

/**
 * Groups of ≥2 colorways whose variants resolved to the SAME blank sku — the mechanical signature of
 * the per-colourway blank bug (N Shopify colourways collapsing onto one S&S sku).
 *
 * ⚠️ NOT a warning on its own, which is why this returns groups rather than a verdict. For the 14
 * products whose Shopify option is an ART colourway on one black blank, this is the CORRECT state, and
 * a red alert there would train the operator to dismiss it. Severity belongs to the caller, which knows
 * what the operator DECLARED with the per-colour toggle.
 */
export function colorwaysSharingOneBlank(
  rows: readonly { color?: string | null; resolvedBlankSku?: string }[],
): Array<{ sku: string; colorways: string[] }> {
  const byS = new Map<string, Set<string>>();
  for (const r of rows) {
    if (!r.resolvedBlankSku || !r.color) continue;
    const set = byS.get(r.resolvedBlankSku) ?? new Set<string>();
    set.add(r.color);
    byS.set(r.resolvedBlankSku, set);
  }
  return [...byS.entries()]
    .filter(([, colorways]) => colorways.size >= 2)
    .map(([sku, colorways]) => ({ sku, colorways: [...colorways].sort() }))
    .sort((a, b) => b.colorways.length - a.colorways.length || a.sku.localeCompare(b.sku));
}

/**
 * What ONE colourway will actually pull — the row of the mapping review (P3b).
 *
 * The 50-row size table it replaces answered the wrong question: mapping decisions are made per
 * COLOURWAY, and sizes only multiply the rows. Worse, it put every control three screens below the
 * fold on a real product. This collapses to one row per colourway carrying the whole decision — the
 * garment photo, the blank it resolves to, the design each placement will print — so an operator can
 * confirm "Grey gets the black logo on a heather grey shirt" without reading SKUs.
 */
export type ColorwayPlan = {
  colorway: string;
  /** The variant's OWN Shopify photo when it has one, else the product's featured image. */
  imageUrl: string | null;
  /** How many sizes of this colourway exist (the detail the row rolls up). */
  sizeCount: number;
  /** Human blank identity, when every size of this colourway resolved to ONE colour. */
  blankLabel: string | null;
  /**
   * EVERY distinct blank colour name this colourway resolved to. Separate from `blankLabel` because a
   * null label has two very different causes and one benign-looking remedy: ZERO recorded names is a
   * legacy row that a re-save fixes, while TWO OR MORE means the colourway's own sizes landed on
   * different colours — a real inconsistency that a re-save just re-stamps. Collapsing both to null let
   * the second render as "colour not recorded — re-save to stamp it", which is false and a no-op.
   */
  blankColorNames: string[];
  /** The distinct bare SKUs this colourway resolved to (one per size, normally one colour). */
  blankSkus: string[];
  /** Sizes of this colourway that did NOT resolve, with the first reason given. */
  unresolvedCount: number;
  unresolvedReason: string | null;
  /** Per placement, the design this colourway prints — `null` means it BLOCKS at build. */
  designs: Array<{ location: string; designId: string | null; designName: string | null }>;
  /** Other colourways whose variants resolved to a SKU this one also uses. */
  sharesBlankWith: string[];
};

/**
 * Which design a colourway prints at one placement. MIRRORS `resolvePlacementDesign` in
 * `packages/rest-api/src/services/catalog-mapping.ts` — kept deliberately identical, including the
 * refusal: when a per-colour map exists and the colourway is missing from it, the answer is `null`
 * (blocks at build) and NEVER the default design. A review screen that showed the default here would
 * tell the operator a colourway is fine when the build will refuse it.
 */
export function plannedDesignForColorway(
  draft: Pick<PlacementDraft, 'designId' | 'designByColor'>,
  colorway: string,
): string | null {
  const map = draft.designByColor;
  if (!map || Object.keys(map).length === 0) return draft.designId || null;
  if (!Object.hasOwn(map, colorway)) return null;
  return map[colorway] || null;
}

export function buildColorwayPlans(params: {
  variants: readonly CatalogVariantSummary[];
  drafts: readonly PlacementDraft[];
  designNameById: ReadonlyMap<string, string>;
  productImageUrl?: string | null;
  /** Live per-variant results from the last save, which win over the stored row. */
  liveByVariant?: ReadonlyMap<
    string,
    {
      blankResolveStatus: string;
      resolvedBlankSku?: string;
      reason?: string;
      blankColorName?: string;
    }
  >;
}): ColorwayPlan[] {
  const { variants, drafts, designNameById, productImageUrl, liveByVariant } = params;
  const byColorway = new Map<string, CatalogVariantSummary[]>();
  for (const v of variants) {
    if (!v.color) continue;
    const list = byColorway.get(v.color) ?? [];
    list.push(v);
    byColorway.set(v.color, list);
  }

  // Which colourways each sku serves, so a row can name its collision partners rather than just
  // flagging that one exists — "shares with White" is actionable, "shares" is not.
  const colorwaysBySku = new Map<string, Set<string>>();
  for (const [colorway, rows] of byColorway) {
    for (const v of rows) {
      const sku = liveByVariant?.get(v.variantId)
        ? liveByVariant.get(v.variantId)?.resolvedBlankSku
        : v.resolvedBlankSku;
      if (!sku) continue;
      const set = colorwaysBySku.get(sku) ?? new Set<string>();
      set.add(colorway);
      colorwaysBySku.set(sku, set);
    }
  }

  return [...byColorway.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([colorway, rows]) => {
      const skus = new Set<string>();
      const colourNames = new Set<string>();
      let unresolvedCount = 0;
      let unresolvedReason: string | null = null;
      for (const v of rows) {
        const live = liveByVariant?.get(v.variantId);
        const status = live ? live.blankResolveStatus : v.blankResolveStatus;
        const sku = live ? live.resolvedBlankSku : v.resolvedBlankSku;
        const colourName = live ? live.blankColorName : v.blankColorName;
        if (status === 'RESOLVED' && sku) {
          skus.add(bareBlankSku(sku));
          if (colourName) colourNames.add(colourName);
        } else {
          unresolvedCount += 1;
          unresolvedReason ??= (live ? live.reason : v.blankResolveReason) ?? null;
        }
      }
      const partners = new Set<string>();
      for (const v of rows) {
        const live = liveByVariant?.get(v.variantId);
        const sku = live ? live.resolvedBlankSku : v.resolvedBlankSku;
        for (const other of colorwaysBySku.get(sku ?? '') ?? []) {
          if (other !== colorway) partners.add(other);
        }
      }
      return {
        colorway,
        // The colourway's OWN photo if any of its sizes carries one; the product's only as a fallback,
        // and it is explicitly a fallback — a red featured image beside a Grey row would mislead, so
        // the UI marks a fallback image rather than presenting it as this colourway's garment.
        imageUrl: rows.find((v) => v.imageUrl)?.imageUrl ?? productImageUrl ?? null,
        sizeCount: rows.length,
        blankLabel: colourNames.size === 1 ? ([...colourNames][0] ?? null) : null,
        blankColorNames: [...colourNames].sort(),
        blankSkus: [...skus].sort(),
        unresolvedCount,
        unresolvedReason,
        designs: drafts
          .filter((d) => d.location.trim().length > 0)
          .map((d) => {
            const designId = plannedDesignForColorway(d, colorway);
            return {
              location: d.location,
              designId,
              designName: designId ? (designNameById.get(designId) ?? null) : null,
            };
          }),
        sharesBlankWith: [...partners].sort(),
      };
    });
}

/** True when this colourway's photo is the product's featured image rather than its own. */
export function usesFallbackImage(
  plan: Pick<ColorwayPlan, 'imageUrl'>,
  productImageUrl: string | null | undefined,
): boolean {
  return plan.imageUrl != null && plan.imageUrl === (productImageUrl ?? null);
}

/**
 * Rank the shop's derived style pairs for THIS product: the pair used by products of the same apparel
 * kind first, then by how many products use it. Ranking here rather than server-side is what keeps the
 * endpoint product-agnostic and cacheable for everyone.
 */
export function rankStylePairs<
  T extends { productCount: number; apparelKinds: string[]; blankStyle: string },
>(pairs: readonly T[], apparelKind: string | null | undefined): T[] {
  const matches = (p: T) => (apparelKind ? p.apparelKinds.includes(apparelKind) : false);
  return [...pairs].sort(
    (a, b) =>
      Number(matches(b)) - Number(matches(a)) ||
      b.productCount - a.productCount ||
      a.blankStyle.localeCompare(b.blankStyle),
  );
}

// ── Caps mapping (caps-materials Phase 2b) ──

/** One S&S (colour × size) variant with its canonical SKU — the `/garments/by-style/{style}/variants`
 *  row the caps editor picks from. */
export type StyleVariant =
  components['schemas']['StyleVariantsResponse']['data']['variants'][number];

/**
 * The single S&S SKU for a caps mapping: the picked style's variant matching the chosen colour + size.
 * Case/whitespace-insensitive (S&S spells colours/sizes inconsistently across rows). Returns null until
 * BOTH a colour and a size are chosen AND they match a real variant — the caps editor gates Save on a
 * non-null result, so the SKU is always "validated by selection", never free-text.
 */
export function resolveCapVariantSku(
  variants: readonly StyleVariant[],
  color: string,
  size: string,
): string | null {
  const c = color.trim().toLowerCase();
  const s = size.trim().toLowerCase();
  if (!c || !s) return null;
  const hit = variants.find(
    (v) => v.colorName.trim().toLowerCase() === c && v.sizeName.trim().toLowerCase() === s,
  );
  return hit?.sku ?? null;
}

// ── Blank identity labels (both caps + apparel) ──

/** Drop the `BLANK#` prefix a resolved-blank sku carries → the bare S&S SKU. */
export function bareBlankSku(sku: string): string {
  return sku.startsWith('BLANK#') ? sku.slice('BLANK#'.length) : sku;
}

/**
 * Human label for an S&S blank from its denormalized identity: "Richardson 112 · Black / OSFA".
 * Returns undefined when no name parts are present (caller falls back to the bare SKU). Used by the caps
 * grid + the apparel variant table so a mapped blank never reads as an opaque number.
 */
export function blankIdentityLabel(n: {
  brandName?: string | null;
  styleName?: string | null;
  colorName?: string | null;
  sizeName?: string | null;
}): string | undefined {
  const style = [n.brandName, n.styleName].filter(Boolean).join(' ');
  const variant = [n.colorName, n.sizeName].filter(Boolean).join(' / ');
  const parts = [style, variant].filter((s) => s.length > 0);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}
